// src/components/public_utilities/UtilityStatusGrid.js
//
// --- UPDATE: Production fixes + performance pass:
//   A. AdvancedUtilityStatusDebugger: sessionStorage I/O batched via 300ms debounce
//      (was: JSON.parse + JSON.stringify + setItem synchronously on every log call)
//   B. canEditUtilities + onEditUtility props accepted and wired to edit buttons on cards
//      (was: parent passes both props; component silently ignored them — no edit UI)
//   C. previousUtilities tracking replaced with useRef Map — O(1) lookups, zero extra renders
//      (was: useState + useEffect with .find() inside .forEach() = O(n²) + double-render)
//   D. statistics.criticalAlerts useEffect dep → statistics.critical (stable number)
//      (was: new array reference every render → effect fired unconditionally)
//   E. handleSummaryClick: functional setState removes selectedStatus from useCallback deps
//      (was: new function reference on every selection → all child elements re-rendered)
//   F. isMounted ref guard in handleRefresh — prevents setState on unmounted component
//   G. normaliseStatus pure helper — removes per-item inline string replacement in memos
//   H. PropTypes updated for new canEditUtilities + onEditUtility props
//
// --- UPDATE (v2 live + AI):
//   I. FIX: live updates were dropped unless the payload carried `type:'utility_status_update'`
//      — the event name already says what it is, so any payload on that channel is accepted
//      and normalised to { type, utility } before reaching onStatusChange.
//   J. Live patch: a WS status change shows on the card immediately (pulsing "LIVE" outline)
//      without waiting for the page to refetch; cleared once the props catch up.
//   K. Connection indicator works with both service styles ('status' channel and
//      on('connect'/'disconnect')) and starts from webSocketService.isConnected().
//      `liveConnected` prop lets the page share its own connection state.
//   L. WS handlers read onStatusChange through a ref — subscribe once, no churn.
//   M. Refresh button calls `onRefresh` (real reload) when provided; it only simulated a delay.
//   N. AI on each card: `getUtilityAi(utility)` → { insights, atRiskAssets, summary } shows the
//      AI view (open insights, affected assets in poor health); warning / critical cards explain
//      in plain language what the status means for residents and crews.
//
// --- UPDATE (Public Utilities v3)
//   O. The logger printed to the browser console on every render in production (render, filter and
//      statistics objects each time) — console output is development-only now.
//   P. "Needs attention now" (AI, from the cards themselves): critical outages by name, warnings and
//      planned maintenance in one sentence, with a click to filter — shown only when something is wrong.
//   Q. Keyboard: Enter and Space activate cards and summary tiles (onKeyDown; onKeyPress is deprecated).
// --- END UPDATE

import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import webSocketService from '../../services/websocketService';
import './UtilityStatusGrid.css';

// ============================================================================
// ADVANCED DEBUG UTILITY
// --- UPDATE A: sessionStorage writes debounced at 300ms.
//   Was: every log() call did a full synchronous JSON.parse + JSON.stringify +
//        sessionStorage.setItem — blocking the main thread on every network event,
//        render count increment, and WS message.
//   Now: entries are buffered; a single flush fires 300ms after the last log call.
// ============================================================================
class AdvancedUtilityStatusDebugger {
    constructor() {
        this.logs              = [];
        this.maxLogs           = 1000;
        this.renderCount       = 0;
        this.updateCount       = 0;
        this.errorCount        = 0;
        this.warningCount      = 0;
        this.startTime         = performance.now();
        this.performanceMarks  = new Map();
        // --- UPDATE A: debounce buffer
        this._ssPending        = [];
        this._ssTimer          = null;
    }

    // --- UPDATE A: deferred flush — zero synchronous I/O in hot paths
    _scheduleFlush(entry) {
        this._ssPending.push(entry);
        if (this._ssTimer) clearTimeout(this._ssTimer);
        this._ssTimer = setTimeout(() => {
            try {
                const recentLogs = this.logs.slice(-100);
                sessionStorage.setItem('utility_grid_logs', JSON.stringify(recentLogs));
                this._ssPending = [];
            } catch (e) {
                console.warn('Failed to persist logs:', e);
                this._ssPending = [];
            }
            this._ssTimer = null;
        }, 300);
    }

    log(level, action, data = {}) {
        const logEntry = {
            timestamp: new Date().toISOString(),
            level,
            action,
            component: 'UtilityStatusGrid',
            ...data,
            renderCount:     this.renderCount,
            sessionDuration: ((performance.now() - this.startTime) / 1000).toFixed(2) + 's',
        };

        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) this.logs.shift();

        if (level === 'ERROR')   this.errorCount++;
        if (level === 'WARNING') this.warningCount++;

        const colors = { INFO: '#2196F3', SUCCESS: '#4CAF50', WARNING: '#FF9800', ERROR: '#F44336', DEBUG: '#9C27B0', PERFORMANCE: '#00BCD4' };
        const emoji  = { INFO: 'ℹ️', SUCCESS: '✅', WARNING: '⚠️', ERROR: '❌', DEBUG: '🔍', PERFORMANCE: '⚡' };

        // --- UPDATE O: development only
        if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.__DEBUG__)) {
            console.log(
                `%c${emoji[level] || '📝'} [${level}] ${action}`,
                `color:${colors[level]};font-weight:bold;font-size:12px;padding:2px 6px;background:${colors[level]}15;border-radius:3px;`,
                data
            );
        }
        // --- END UPDATE

        // --- UPDATE A: was synchronous I/O; now deferred
        this._scheduleFlush(logEntry);
    }

    startPerformance(marker) { this.performanceMarks.set(marker, performance.now()); }
    endPerformance(marker, action) {
        const start = this.performanceMarks.get(marker);
        if (start) {
            const duration = performance.now() - start;
            this.log('PERFORMANCE', action, { duration: `${duration.toFixed(2)}ms`, marker, threshold: duration > 50 ? 'SLOW' : 'NORMAL' });
            this.performanceMarks.delete(marker);
            return duration;
        }
        return null;
    }

    incrementRender() { this.renderCount++; }
    incrementUpdate() { this.updateCount++; }

    getStats() {
        return {
            totalLogs:       this.logs.length,
            renderCount:     this.renderCount,
            updateCount:     this.updateCount,
            errorCount:      this.errorCount,
            warningCount:    this.warningCount,
            sessionDuration: ((performance.now() - this.startTime) / 1000).toFixed(2) + 's',
            recentLogs:      this.logs.slice(-20),
        };
    }

    exportLogs() {
        const dataStr = JSON.stringify({ logs: this.logs, stats: this.getStats(), exportTime: new Date().toISOString() }, null, 2);
        const url  = URL.createObjectURL(new Blob([dataStr], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url; link.download = `utility-grid-debug-${Date.now()}.json`; link.click();
        URL.revokeObjectURL(url);
        this.log('INFO', 'logs_exported', { count: this.logs.length });
    }

    clearLogs() {
        const clearedCount = this.logs.length;
        this.logs = [];
        sessionStorage.removeItem('utility_grid_logs');
        this.log('INFO', 'logs_cleared', { clearedCount });
    }
}

const gridDebugger = new AdvancedUtilityStatusDebugger();

if (typeof window !== 'undefined') {
    window.__UtilityStatusGridDebug__ = {
        getLogs:    () => gridDebugger.logs,
        getStats:   () => gridDebugger.getStats(),
        exportLogs: () => gridDebugger.exportLogs(),
        clearLogs:  () => gridDebugger.clearLogs(),
        debugger:   gridDebugger,
    };
}

// ============================================================================
// ERROR BOUNDARY
// ============================================================================
class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }

    static getDerivedStateFromError(error) { return { hasError: true }; }

    componentDidCatch(error, errorInfo) {
        gridDebugger.log('ERROR', 'component_error_boundary', { error: error.toString(), componentStack: errorInfo.componentStack });
        this.setState({ error });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="utility-status-grid-container">
                    <div className="grid-error">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div className="grid-error-content">
                            <div className="grid-error-title">Something went wrong</div>
                            <div className="grid-error-message">{this.state.error && this.state.error.toString()}</div>
                        </div>
                        <button onClick={() => window.location.reload()}>Reload Page</button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusColor = (status) => {
    const colors = {
        operational: '#4CAF50', online: '#4CAF50', active: '#4CAF50',
        warning: '#FF9800', degraded: '#FF9800', partial_outage: '#FF9800',
        critical: '#F44336', error: '#F44336', full_outage: '#F44336',
        offline: '#9E9E9E', maintenance: '#2196F3', pending: '#00BCD4', unknown: '#9E9E9E',
    };
    return colors[status?.toLowerCase()] || '#9E9E9E';
};

const getStatusIcon = (status) => {
    const icons = {
        operational: 'check-circle', online: 'check-circle', active: 'check-circle',
        warning: 'exclamation-triangle', degraded: 'exclamation-triangle', partial_outage: 'exclamation-triangle',
        critical: 'times-circle', error: 'times-circle', full_outage: 'times-circle',
        offline: 'power-off', maintenance: 'wrench', pending: 'clock', unknown: 'question-circle',
    };
    return icons[status?.toLowerCase()] || 'question-circle';
};

// --- UPDATE G: pure normaliseStatus helper — replaces the 3-line inline
//   string replacement that ran on every utility in filteredUtilities AND statistics.
//   Now called once per utility and memoised via the utilities prop Map.
const normaliseStatus = (raw) => {
    const s = raw?.toLowerCase() || 'unknown';
    if (s === 'partial_outage') return 'warning';
    if (s === 'full_outage')   return 'critical';
    if (s === 'unknown')       return 'offline';
    return s;
};

const retryOperation = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await fn();
        } catch (error) {
            if (i === maxRetries - 1) throw error;
            gridDebugger.log('WARNING', 'retry_attempt', { attempt: i + 1, maxRetries, delay: `${delay}ms` });
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================
const LoadingState = memo(() => (
    <div className="grid-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading utility status...</div>
    </div>
));
LoadingState.displayName = 'LoadingState';

const ErrorState = memo(({ error, onRetry }) => (
    <div className="grid-error">
        <i className="fas fa-exclamation-triangle"></i>
        <div className="grid-error-content">
            <div className="grid-error-title">Error Loading Utilities</div>
            <div className="grid-error-message">{error}</div>
        </div>
        {onRetry && <button onClick={onRetry}>Retry</button>}
    </div>
));
ErrorState.displayName = 'ErrorState';

const EmptyState = memo(({ message = 'No utilities found' }) => (
    <div className="grid-empty">
        <i className="fas fa-inbox"></i>
        <p>{message}</p>
    </div>
));
EmptyState.displayName = 'EmptyState';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
// --- UPDATE N: plain-language status explanations
const STATUS_EXPLAIN = {
    warning:     'Partial outage or degraded service — some customers may see low pressure or interruptions.',
    critical:    'Major outage — service is down for many customers. Crews should be dispatched now.',
    maintenance: 'Planned maintenance — interruptions are expected and should be communicated to residents.',
    offline:     'No status reported — check telemetry and contact the operator.',
};
const LIVE_FLASH_MS = 6000;
// --- END UPDATE

const UtilityStatusGrid = ({
    utilities: utilitiesProp = [], // --- UPDATE J: merged with live patches below
    onUtilityClick,
    autoRefresh      = true,
    refreshInterval  = 30000,
    enableWebSocket  = false,
    webSocketUrl     = null,
    onStatusChange,
    onError,
    maxRetries       = 3,
    showMetrics      = false,
    // --- UPDATE B: new props accepted (were silently ignored before)
    canEditUtilities = false,
    onEditUtility,
    // --- UPDATE
    onRefresh,
    getUtilityAi,
    liveConnected,
    // --- END UPDATE
}) => {
    gridDebugger.incrementRender();
    gridDebugger.log('INFO', 'component_render', { utilityCount: utilitiesProp.length, autoRefresh, enableWebSocket }); // --- UPDATE: raw prop (merged list is built below)

    // ========================================================================
    // STATE
    // ========================================================================
    const [selectedStatus,   setSelectedStatus]   = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [lastUpdate,       setLastUpdate]        = useState(new Date());
    const [loading,          setLoading]           = useState(false);
    const [error,            setError]             = useState(null);
    const [isConnected,      setIsConnected]       = useState(false);

    const refreshTimerRef = useRef(null);
    // --- UPDATE F: isMounted guard prevents setState on unmounted component
    const isMountedRef    = useRef(true);
    // --- UPDATE C: previous-utility tracking via ref Map — no state, no double-render,
    //   O(1) lookups replace the O(n²) previousUtilities.find() inside forEach
    const prevUtilitiesMapRef = useRef(new Map());

    // --- UPDATE J/L: live overrides + stable callback ref
    const [liveOverrides, setLiveOverrides] = useState(() => new Map());
    const [flashIds, setFlashIds] = useState(() => new Set());
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;
    const utilities = useMemo(() => {
        if (!liveOverrides.size) return utilitiesProp;
        return utilitiesProp.map(u => (liveOverrides.has(u.id) ? { ...u, ...liveOverrides.get(u.id) } : u));
    }, [utilitiesProp, liveOverrides]);
    // Drop overrides once the server data agrees
    useEffect(() => {
        if (!liveOverrides.size) return;
        let changed = false;
        const next = new Map(liveOverrides);
        for (const u of utilitiesProp) {
            const o = next.get(u.id);
            if (o && o.status === u.status) { next.delete(u.id); changed = true; }
        }
        if (changed) setLiveOverrides(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [utilitiesProp]);
    const connected = liveConnected ?? isConnected;
    // --- END UPDATE

    // ========================================================================
    // --- UPDATE G: pre-normalise statuses once per utilities change.
    //   Maps utility.id → normalised status string.
    //   Used by both filteredUtilities and statistics to avoid repeated inline
    //   string replacements on every filter/sort pass.
    // ========================================================================
    const normalisedStatusMap = useMemo(() => {
        const map = new Map();
        for (const u of utilities) map.set(u.id, normaliseStatus(u.status));
        return map;
    }, [utilities]);

    // ========================================================================
    // MEMOIZED VALUES
    // ========================================================================
    const filteredUtilities = useMemo(() => {
        gridDebugger.startPerformance('filter_utilities');

        let filtered = utilities;

        if (selectedStatus !== 'all') {
            // --- UPDATE G: uses pre-normalised Map — no inline string replacement
            filtered = filtered.filter(u => normalisedStatusMap.get(u.id) === selectedStatus);
        }
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(u => u.category === selectedCategory);
        }

        gridDebugger.endPerformance('filter_utilities', 'utilities_filtered');
        gridDebugger.log('DEBUG', 'utilities_filtered', { total: utilities.length, filtered: filtered.length, status: selectedStatus, category: selectedCategory });

        return filtered;
    }, [utilities, selectedStatus, selectedCategory, normalisedStatusMap]);

    const statistics = useMemo(() => {
        gridDebugger.startPerformance('calculate_statistics');

        const stats = {
            total: utilities.length,
            operational: 0, warning: 0, critical: 0, maintenance: 0, offline: 0,
            byCategory: {}, criticalAlerts: [],
        };

        for (const utility of utilities) {
            // --- UPDATE G: O(1) Map lookup replaces inline replacement
            const status = normalisedStatusMap.get(utility.id) || 'offline';

            if (status in stats) stats[status]++;
            if (status === 'critical') stats.criticalAlerts.push(utility);

            const cat = utility.category || 'other';
            if (!stats.byCategory[cat]) stats.byCategory[cat] = { total: 0, operational: 0, issues: 0, health: 0 };
            stats.byCategory[cat].total++;
            if (status === 'operational') stats.byCategory[cat].operational++;
            else if (status === 'warning' || status === 'critical') stats.byCategory[cat].issues++;
            stats.byCategory[cat].health = ((stats.byCategory[cat].operational / stats.byCategory[cat].total) * 100).toFixed(1);
        }

        gridDebugger.endPerformance('calculate_statistics', 'statistics_calculated');
        gridDebugger.log('DEBUG', 'statistics', stats);

        return stats;
    }, [utilities, normalisedStatusMap]);

    // ========================================================================
    // CALLBACKS
    // ========================================================================
    const handleError = useCallback((errorMsg, details = {}) => {
        gridDebugger.log('ERROR', 'error_occurred', { errorMsg, ...details });
        setError(errorMsg);
        if (onError) onError(new Error(errorMsg), details);
    }, [onError]);

    const handleUtilityClick = useCallback((utility) => {
        gridDebugger.log('INFO', 'utility_clicked', { utilityId: utility.id, status: utility.status, category: utility.category });
        if (onUtilityClick) {
            try { onUtilityClick(utility); }
            catch (error) { handleError('Failed to handle utility click', { utility, error }); }
        }
    }, [onUtilityClick, handleError]);

    // --- UPDATE B: edit handler — wires onEditUtility to the card button
    const handleEditUtility = useCallback((e, utility) => {
        e.stopPropagation(); // don't trigger handleUtilityClick
        gridDebugger.log('INFO', 'utility_edit_clicked', { utilityId: utility.id });
        if (onEditUtility) {
            try { onEditUtility(utility); }
            catch (err) { handleError('Failed to open edit modal', { utility, err }); }
        }
    }, [onEditUtility, handleError]);

    const handleRefresh = useCallback(async () => {
        gridDebugger.log('INFO', 'manual_refresh_triggered');
        gridDebugger.incrementUpdate();
        // --- UPDATE F: guard setLoading / setError against unmounted component
        if (!isMountedRef.current) return;
        setLoading(true);
        setError(null);

        try {
            await retryOperation(async () => {
                // --- UPDATE M: real reload when the page provides one
                if (onRefresh) await onRefresh();
                else await new Promise(resolve => setTimeout(resolve, 500));
                // --- END UPDATE
                if (isMountedRef.current) setLastUpdate(new Date());
            }, maxRetries);
            gridDebugger.log('SUCCESS', 'refresh_completed');
        } catch (err) {
            gridDebugger.log('ERROR', 'refresh_failed', { error: err.message });
            if (isMountedRef.current) handleError('Failed to refresh utility status');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [maxRetries, handleError, onRefresh]);

    const clearError = useCallback(() => { setError(null); gridDebugger.log('INFO', 'error_cleared'); }, []);

    // --- UPDATE E: functional setState removes selectedStatus from deps —
    //   was: useCallback([selectedStatus]) → new ref on every click → child re-renders
    const handleSummaryClick = useCallback((status) => {
        gridDebugger.log('INFO', 'summary_item_clicked', { status });
        setSelectedStatus(prev => prev === status ? 'all' : status);
    }, []); // no deps needed with functional update

    // ========================================================================
    // WEBSOCKET EFFECTS
    // ========================================================================
    // --- UPDATE I/J/K/L: accept any payload on the channel, patch the card, subscribe once
    useEffect(() => {
        if (!enableWebSocket) return;

        try { if (webSocketService.isConnected?.()) setIsConnected(true); } catch { /* noop */ }

        const handleConnectionStatus = (event) => {
            gridDebugger.log('INFO', 'websocket_service_status', { event });
            const status = event?.status || event;
            if (status === 'connected') { setIsConnected(true); gridDebugger.log('SUCCESS', 'websocket_connected_via_service'); }
            else if (status === 'disconnected' || status === 'error') {
                setIsConnected(false);
                if (status === 'error') gridDebugger.log('ERROR', 'websocket_service_error', { error: event?.error });
            }
        };
        const onConnect = () => setIsConnected(true);
        const onDisconnect = () => setIsConnected(false);

        const handleDataUpdate = (raw) => {
            const data = raw || {};
            const utility = data.utility || data.data || (data.id != null ? data : null);
            gridDebugger.log('INFO', 'received_ws_data', { type: data.type, id: utility?.id });
            if (data.type && data.type !== 'utility_status_update') return; // a different message on the channel
            const normalised = { ...data, type: 'utility_status_update', utility, data: data.data || utility };
            gridDebugger.log('INFO', 'processing_utility_update', normalised);
            setIsConnected(true);
            setLastUpdate(new Date());
            const id = utility?.id ?? data.utility_id;
            if (id != null && (utility?.status || data.status)) {
                const patch = { status: utility?.status || data.status };
                if (utility?.message || data.message) patch.message = utility?.message || data.message;
                setLiveOverrides(prev => new Map(prev).set(id, patch));
                setFlashIds(prev => new Set(prev).add(id));
                setTimeout(() => {
                    if (!isMountedRef.current) return;
                    setFlashIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                }, LIVE_FLASH_MS);
            }
            onStatusChangeRef.current?.(normalised);
        };

        gridDebugger.log('INFO', 'subscribing_to_websocket_service');
        webSocketService.subscribe('status', handleConnectionStatus);
        webSocketService.subscribe('utility_status_update', handleDataUpdate);
        webSocketService.on?.('connect', onConnect);
        webSocketService.on?.('disconnect', onDisconnect);
        webSocketService.connect();

        return () => {
            gridDebugger.log('INFO', 'unsubscribing_from_websocket_service');
            webSocketService.unsubscribe('status', handleConnectionStatus);
            webSocketService.unsubscribe('utility_status_update', handleDataUpdate);
            webSocketService.off?.('connect', onConnect);
            webSocketService.off?.('disconnect', onDisconnect);
        };
    }, [enableWebSocket]);
    // --- END UPDATE

    // ========================================================================
    // LIFECYCLE EFFECTS
    // ========================================================================
    useEffect(() => {
        gridDebugger.log('INFO', 'component_mounted');
        isMountedRef.current = true; // --- UPDATE F
        return () => {
            isMountedRef.current = false; // --- UPDATE F
            gridDebugger.log('INFO', 'component_unmounted');
            if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!autoRefresh) return;
        gridDebugger.log('INFO', 'auto_refresh_enabled', { interval: `${refreshInterval / 1000}s` });
        refreshTimerRef.current = setInterval(() => {
            gridDebugger.log('DEBUG', 'auto_refresh_triggered');
            gridDebugger.incrementUpdate();
            setLastUpdate(new Date());
        }, refreshInterval);
        return () => { if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); gridDebugger.log('INFO', 'auto_refresh_disabled'); } };
    }, [autoRefresh, refreshInterval]);

    // --- UPDATE C: O(1) previous-status change detection via ref Map.
    //   Was: useState(previousUtilities) + .find() inside .forEach() = O(n²).
    //        Also: `previousUtilities` in useEffect deps caused a double-render
    //        on every `utilities` change (state update → re-run → state update).
    //   Now: useRef(Map) — no state, no extra render, O(1) per utility lookup.
    useEffect(() => {
        const prevMap = prevUtilitiesMapRef.current;

        for (const utility of utilities) {
            const prevStatus = prevMap.get(utility.id);
            if (prevStatus !== undefined && prevStatus !== utility.status) {
                gridDebugger.log('WARNING', 'status_changed', {
                    utilityId:   utility.id,
                    utilityName: utility.name,
                    from:        prevStatus,
                    to:          utility.status,
                });
                // --- UPDATE L
                onStatusChangeRef.current?.({ utility, previousStatus: prevStatus, newStatus: utility.status });
            }
        }

        // Rebuild the Map for the next comparison — one O(n) pass, no state update
        prevUtilitiesMapRef.current = new Map(utilities.map(u => [u.id, u.status]));
    }, [utilities]);

    // --- UPDATE D: dep is statistics.critical (stable number) not criticalAlerts (new array ref)
    useEffect(() => {
        if (statistics.critical > 0) {
            gridDebugger.log('WARNING', 'critical_utilities_detected', {
                count:     statistics.critical,
                utilities: statistics.criticalAlerts.map(u => u.name),
            });
        }
    }, [statistics.critical]); // was statistics.criticalAlerts — new array every render

    // ========================================================================
    // RENDER
    // ========================================================================
    return (
        <ErrorBoundary>
            <div className="utility-status-grid-container">
                {/* Header */}
                <div className="grid-header">
                    <h3>Utility Status Overview</h3>
                    <span className={`last-update ${connected ? 'syncing' : ''}`}>
                        {/* --- UPDATE K: live dot */}
                        <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, background: connected ? '#4CAF50' : '#9E9E9E', boxShadow: connected ? '0 0 6px #4CAF50' : 'none' }} />
                        Last updated: {lastUpdate.toLocaleTimeString()}
                        {connected ? ' • Live' : ' • Live updates offline'}
                    </span>
                </div>

                {/* Error Display */}
                {error && !loading && <ErrorState error={error} onRetry={handleRefresh} />}

                {/* --- UPDATE P: what needs attention, from the cards themselves */}
                {(statistics.critical > 0 || statistics.warning > 0) && (
                    <div role="status" data-testid="utility-attention" style={{ margin: '8px 0 12px', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(128,90,213,0.35)', background: 'rgba(128,90,213,0.07)', fontSize: 13 }}>
                        <span style={{ fontWeight: 800, color: '#805AD5' }}>✦ Needs attention now: </span>
                        {statistics.critical > 0 && (
                            <span>
                                <button type="button" onClick={() => handleSummaryClick('critical')} style={{ all: 'unset', cursor: 'pointer', color: '#E53E3E', fontWeight: 700, textDecoration: 'underline' }}>
                                    {statistics.critical} major outage{statistics.critical === 1 ? '' : 's'}
                                </button>
                                {' '}({statistics.criticalAlerts.slice(0, 3).map(u => u.name).join(', ')}{statistics.critical > 3 ? '…' : ''}) — dispatch crews and tell residents.{' '}
                            </span>
                        )}
                        {statistics.warning > 0 && (
                            <span>
                                <button type="button" onClick={() => handleSummaryClick('warning')} style={{ all: 'unset', cursor: 'pointer', color: '#DD6B20', fontWeight: 700, textDecoration: 'underline' }}>
                                    {statistics.warning} partial outage{statistics.warning === 1 ? '' : 's'}
                                </button>{' '}to watch.{' '}
                            </span>
                        )}
                        {statistics.maintenance > 0 && <span>{statistics.maintenance} under planned maintenance.</span>}
                    </div>
                )}
                {/* --- END UPDATE */}

                {/* Statistics Summary */}
                <div className="status-summary">
                    {[
                        { key: 'operational', color: '#4CAF50', label: 'Operational' },
                        { key: 'warning',     color: '#FF9800', label: 'Warning'     },
                        { key: 'critical',    color: '#F44336', label: 'Critical'    },
                        { key: 'maintenance', color: '#2196F3', label: 'Maintenance' },
                        { key: 'offline',     color: '#9E9E9E', label: 'Offline'     },
                    ].map(({ key, color, label }) => (
                        <div
                            key={key}
                            className={`summary-item${selectedStatus === key ? ' active' : ''}`}
                            style={{ borderColor: color }}
                            onClick={() => handleSummaryClick(key)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSummaryClick(key); } }} /* --- UPDATE Q */
                        >
                            <span className="summary-count">{statistics[key]}</span>
                            <span className="summary-label">{label}</span>
                            {key === 'critical' && statistics.critical > 0 && <span className="notification-badge">!</span>}
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="grid-filters">
                    <select
                        value={selectedStatus}
                        onChange={(e) => { setSelectedStatus(e.target.value); gridDebugger.log('INFO', 'status_filter_changed', { value: e.target.value }); }}
                        className="form-control form-control-sm"
                    >
                        <option value="all">All Statuses</option>
                        <option value="operational">Operational</option>
                        <option value="warning">Warning</option>
                        <option value="critical">Critical</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="offline">Offline</option>
                    </select>

                    <select
                        value={selectedCategory}
                        onChange={(e) => { setSelectedCategory(e.target.value); gridDebugger.log('INFO', 'category_filter_changed', { value: e.target.value }); }}
                        className="form-control form-control-sm"
                    >
                        <option value="all">All Categories</option>
                        {Object.keys(statistics.byCategory).map(cat => (
                            <option key={cat} value={cat}>{cat} ({statistics.byCategory[cat].total})</option>
                        ))}
                    </select>

                    <button
                        onClick={handleRefresh}
                        className="btn btn-sm btn-primary"
                        disabled={loading}
                        style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.3s', boxShadow: '0 2px 8px rgba(33,150,243,0.3)' }}
                    >
                        <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>{' '}
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>

                {/* Grid */}
                <div className="utility-grid">
                    {loading ? (
                        <LoadingState />
                    ) : filteredUtilities.length === 0 ? (
                        <EmptyState message={selectedStatus !== 'all' || selectedCategory !== 'all' ? 'No utilities match your filters' : 'No utilities found'} />
                    ) : (
                        filteredUtilities.map(utility => {
                            const statusLower = utility.status?.toLowerCase();
                            const cardClass   = `utility-card status-${statusLower}`;
                            // --- UPDATE J/N
                            const isFlashing  = flashIds.has(utility.id);
                            const ai          = getUtilityAi ? getUtilityAi(utility) : null;
                            const explain     = STATUS_EXPLAIN[normalisedStatusMap.get(utility.id)];
                            // --- END UPDATE

                            return (
                                <div
                                    key={utility.id}
                                    className={cardClass}
                                    onClick={() => handleUtilityClick(utility)}
                                    style={{ borderLeftColor: getStatusColor(utility.status), position: 'relative', ...(isFlashing ? { boxShadow: `0 0 0 3px ${getStatusColor(utility.status)}55`, transition: 'box-shadow 0.3s' } : {}) }}
                                    data-live={isFlashing ? 'true' : undefined}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleUtilityClick(utility); } }} /* --- UPDATE Q */
                                >
                                    {/* --- UPDATE J: live badge */}
                                    {isFlashing && (
                                        <span style={{ position: 'absolute', top: 10, left: 12, fontSize: 10, fontWeight: 800, color: '#fff', background: getStatusColor(utility.status), borderRadius: 4, padding: '2px 6px' }}>LIVE UPDATE</span>
                                    )}
                                    {/* --- END UPDATE */}
                                    {/* Critical Badge */}
                                    {statusLower === 'critical' && <span className="notification-badge">URGENT</span>}

                                    {/* --- UPDATE B: Edit button — only rendered when admin/staff,
                                        wired to onEditUtility. stopPropagation prevents card click. */}
                                    {canEditUtilities && onEditUtility && (
                                        <button
                                            className="utility-edit-btn"
                                            onClick={(e) => handleEditUtility(e, utility)}
                                            title={`Edit ${utility.name}`}
                                            style={{
                                                position: 'absolute', top: '10px', right: statusLower === 'critical' ? '72px' : '12px',
                                                padding: '4px 8px', background: 'rgba(33,150,243,0.12)',
                                                border: '1px solid #2196F3', borderRadius: '6px',
                                                color: '#2196F3', cursor: 'pointer', fontSize: '12px',
                                                fontWeight: 600, lineHeight: 1, transition: 'all 0.2s',
                                                zIndex: 2,
                                            }}
                                        >
                                            <i className="fas fa-edit" style={{ marginRight: '4px' }}></i>
                                            Edit
                                        </button>
                                    )}

                                    {/* Header */}
                                    <div className="utility-header">
                                        <h5>{utility.name}</h5>
                                        <i
                                            className={`fas fa-${getStatusIcon(utility.status)} status-icon ${statusLower}`}
                                            style={{ color: getStatusColor(utility.status) }}
                                        ></i>
                                    </div>

                                    {/* Info */}
                                    <div className="utility-info">
                                        <div className="info-item">
                                            <span className="info-label">Category:</span>
                                            <span className="info-value">{utility.category || 'N/A'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Status:</span>
                                            <span className="info-value" style={{ color: getStatusColor(utility.status) }}>
                                                {(utility.status || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        </div>
                                        {utility.lastCheck && (
                                            <div className="info-item">
                                                <span className="info-label">Last Check:</span>
                                                <span className="info-value">
                                                    {new Date(utility.lastCheck).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        )}
                                        {utility.uptime !== undefined && (
                                            <div className="info-item">
                                                <span className="info-label">Uptime:</span>
                                                <span className="info-value">{utility.uptime}%</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* --- UPDATE N: what the status means + AI view */}
                                    {explain && (
                                        <div style={{ fontSize: 12, marginTop: 8, padding: '6px 8px', borderRadius: 6, background: `${getStatusColor(utility.status)}14`, color: 'inherit' }}>
                                            {explain}
                                        </div>
                                    )}
                                    {ai && (ai.summary || ai.insights?.length > 0 || ai.atRiskAssets > 0) && (
                                        <div className="utility-ai" aria-label={`AI view for ${utility.name}`} style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(128,90,213,0.35)', background: 'rgba(128,90,213,0.07)', fontSize: 12 }}>
                                            <div style={{ fontWeight: 800, color: '#805AD5', marginBottom: 4 }}>✦ AI view</div>
                                            {ai.summary && <div style={{ marginBottom: 4 }}>{ai.summary}</div>}
                                            {ai.atRiskAssets > 0 && (
                                                <div style={{ color: '#DD6B20', fontWeight: 600 }}>{ai.atRiskAssets} affected asset{ai.atRiskAssets === 1 ? '' : 's'} in poor health</div>
                                            )}
                                            {(ai.insights || []).slice(0, 2).map((ins, i) => (
                                                <div key={ins.id || i} style={{ marginTop: 4 }}>
                                                    <span style={{ fontWeight: 700, color: ins.severity === 'critical' ? '#E53E3E' : ins.severity === 'warning' ? '#DD6B20' : '#3182CE' }}>●</span>{' '}
                                                    {ins.title}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* --- END UPDATE */}

                                    {/* Metrics */}
                                    {showMetrics && utility.metrics && (
                                        <div className="utility-metrics">
                                            {Object.entries(utility.metrics).map(([key, value]) => (
                                                <div key={key} className="metric-item">
                                                    <span className="metric-value">{value}</span>
                                                    <span className="metric-label">{key}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Message / Affected Assets */}
                                    {(utility.message || utility.description) && (
                                        <div className="utility-message" style={{ flexDirection: 'column' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                <i className="fas fa-info-circle"></i>
                                                <span>{utility.message || utility.description}</span>
                                            </div>
                                            {utility.affected_assets && utility.affected_assets.length > 0 && (
                                                <div style={{ marginTop: '12px', width: '100%', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: getStatusColor(utility.status), textTransform: 'uppercase' }}>
                                                        Affected Assets:
                                                    </span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                                        {utility.affected_assets.map((assetName, idx) => (
                                                            <span key={idx} style={{ background: 'white', color: getStatusColor(utility.status), padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', border: `1px solid ${getStatusColor(utility.status)}` }}>
                                                                <i className="fas fa-exclamation-circle" style={{ marginRight: '4px', fontSize: '10px' }}></i>
                                                                {assetName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Debug Panel */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="grid-debug-panel">
                        <details>
                            <summary>Debug Stats &amp; Performance</summary>
                            <div>
                                <strong>Renders:</strong> {gridDebugger.renderCount}<br />
                                <strong>Updates:</strong> {gridDebugger.updateCount}<br />
                                <strong>Errors:</strong>  {gridDebugger.errorCount}<br />
                                <strong>Session:</strong> {((performance.now() - gridDebugger.startTime) / 1000).toFixed(1)}s<br />
                                <strong>WebSocket:</strong> {connected ? 'Connected' : 'Disconnected'}<br />
                                <strong>Live patches:</strong> {liveOverrides.size}<br />
                                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                                    <button onClick={() => gridDebugger.exportLogs()} style={{ padding: '6px 12px', background: 'rgba(0,255,136,0.1)', border: '1px solid #00ff88', color: '#00ff88', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Export Logs</button>
                                    <button onClick={() => gridDebugger.clearLogs()} style={{ padding: '6px 12px', background: 'rgba(244,67,54,0.1)', border: '1px solid #F44336', color: '#F44336', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Clear Logs</button>
                                </div>
                            </div>
                        </details>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

// ============================================================================
// PROP TYPES — UPDATE H: added canEditUtilities + onEditUtility
// ============================================================================
UtilityStatusGrid.propTypes = {
    utilities: PropTypes.arrayOf(PropTypes.shape({
        id:               PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name:             PropTypes.string.isRequired,
        category:         PropTypes.string,
        status:           PropTypes.string.isRequired,
        lastCheck:        PropTypes.string,
        message:          PropTypes.string,
        description:      PropTypes.string,
        affected_assets:  PropTypes.arrayOf(PropTypes.string),
        uptime:           PropTypes.number,
        metrics:          PropTypes.object,
    })),
    onUtilityClick:   PropTypes.func,
    autoRefresh:      PropTypes.bool,
    refreshInterval:  PropTypes.number,
    enableWebSocket:  PropTypes.bool,
    webSocketUrl:     PropTypes.string,
    onStatusChange:   PropTypes.func,
    onError:          PropTypes.func,
    maxRetries:       PropTypes.number,
    showMetrics:      PropTypes.bool,
    // --- UPDATE H: new props documented in PropTypes
    canEditUtilities: PropTypes.bool,
    onEditUtility:    PropTypes.func,
    // --- UPDATE
    onRefresh:        PropTypes.func,
    getUtilityAi:     PropTypes.func,
    liveConnected:    PropTypes.bool,
    // --- END UPDATE
};

export default memo(UtilityStatusGrid);